/**
 * 67.65: a modular building kit's look (DECISIONS 201, BUILDLIST 63.14; the owner: "modular building system, so that we could
 * build bases that are big enough to walk around in and use for storage and build defensive walls and things like that").
 * Presentation only, as a sheet first (design_sheets/base_kit.html); the sim's note (the module as a part with health and a
 * material, the grid, destruction per module) is lane F's.
 *
 * Recognisable modern expeditionary forms (197, 199) in the D style (side profiles extruded and chamfered, bare): framed
 * plywood panels on posts (a B-hut's), corrugated roofs on purlins, Hesco bastions, sandbag parapets, concrete T-walls,
 * triple concertina wire, steel shelving, storage crates, a container fitted out as a room, a guard tower on legs, a ladder, a
 * vehicle gate two cells wide; a plinth that seats a column of cells level on a slope (§5.22's slope rule); the gates' leaves
 * as modules of their own on a named pivot (`GATE_LEAVES`), so the sim turns them shut or open.
 *
 * The grid is lane F's provisional cell (`MODULE_GRID`, in people): a cube 2 people a side. A wall panel spans one cell's
 * edge, centred on it, floor to floor; a floor tile or roof slab is one cell square; a post stands at a grid node. Every
 * module is built at the origin in model units (a person is 1.8, `U`): a wall along x (its faces ±z), its foot at y = 0;
 * a tile centred on the origin; a roof slab at the storey's top (y = the cell's height). Each module has three forms
 * (63.4's: whole, damaged, destroyed). Drawn instanced: one `InstancedMesh` per module and form (`BaseKitField`), its parts
 * merged into one geometry coloured per vertex.
 */
import * as THREE from 'three';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import { markShared } from './meshBake';
import { damageModel } from './damageForms';
import { box, group, mat } from './vehicleModels';
import { slab, front, top, rect, rev } from './vehicleLooks';
import type { UnitPalette } from './unitPalette';
import { REFERENCE } from '../constants';
import { RUBBLE, RUBBLE_KINDS } from '../constants/modules';

/**
 * The kit's grid, in people (lane F's provisional cell, Oct 18; the sim's note settles it): a cell 2 × 2 × 2 (floor to floor:
 * the over-the-shoulder camera clips a 1.5 ceiling); a wall 0.2 thick; a corner post 0.25 square; a floor or roof slab 0.15;
 * a door 1.2 wide × 1.5 tall (0.4 jambs: one body passes the walk's 1-person disc with 0.1 a side); a window 0.70–1.5 up (69.16: a standing eye over its sill, a crouched one under it; DECISIONS 326: 0.75 → 0.70 with 73.3's lower crouch, which it hides whole);
 * a parapet 1 tall (a crouched body hidden, a standing one fires over); a storage crate 1 a side, four to a cell. A room of
 * 3 × 3 cells is 6 × 6 people; a one-cell corridor 1.8 clear, single file. A vehicle gate spans two cells (`MODULE_SPAN`), its
 * opening 3.2 clear between 0.4 jambs (the widest vehicle, the walker's 1.9, with 0.65 a side); a ladder 0.4 wide.
 */
export const MODULE_GRID = { cell: 2, wall: 0.2, post: 0.25, slab: 0.15, door: { width: 1.2, height: 1.5 }, window: { from: 0.7, to: 1.5 }, parapet: 1, crate: 1, vehicleGate: { width: 3.2 }, ladder: { width: 0.4 } } as const;
/** Model units a person (the 'person' ruler the hitboxes and buildingKit's parts are modelled on). */
export const U = 1.8;

export const BASE_MODULES = [
  'wallPlain', 'wallDoor', 'wallWindow', 'wallSlit', 'post', 'postT', 'floor', 'roof', 'stairs', 'gate',
  'tWall', 'hesco', 'sandbags', 'wire', 'shelving', 'crate', 'containerRoom', 'tower', 'ladder', 'gateWide',
  'plinth', 'gateLeaf', 'gateWideLeaf', 'sandbagCorner',
] as const;
export type BaseModule = typeof BASE_MODULES[number];
export type ModuleForm = 'whole' | 'damaged' | 'destroyed';
export const MODULE_FORMS: ModuleForm[] = ['whole', 'damaged', 'destroyed'];
/** What each module is (the sheet's labels). */
export const MODULE_NAMES: Record<BaseModule, string> = {
  wallPlain: 'wall panel', wallDoor: 'wall · door', wallWindow: 'wall · window', wallSlit: 'wall · firing slit', post: 'corner post', postT: 'T-join post',
  floor: 'floor tile', roof: 'roof slab', stairs: 'stairs', gate: 'gate', tWall: 'concrete T-wall', hesco: 'Hesco', sandbags: 'sandbag parapet', wire: 'wire (triple concertina)',
  shelving: 'shelving', crate: 'storage crate', containerRoom: 'container room', tower: 'guard tower', ladder: 'ladder', gateWide: 'vehicle gate (two cells)',
  plinth: 'plinth (stretches)', gateLeaf: 'gate leaf', gateWideLeaf: 'vehicle gate leaf', sandbagCorner: 'sandbag corner',
};
/**
 * Lane F's span class: the modules longer than a cell, in cells along their x (a wall's run); the rest span one. A spanning
 * module is centred on its run like a one-cell one, so `onEdge` and `inCell` place it from its first cell.
 */
export const MODULE_SPAN: Partial<Record<BaseModule, number>> = { gateWide: 2, containerRoom: 2 };
const spanOf = (k: BaseModule): number => MODULE_SPAN[k] ?? 1;

const PLY = '#a8916a', PLY_DARK = '#8c7754', FRAME = '#6e5a3e', CONCRETE = '#9a978d', CONCRETE_DARK = '#7f7c73', STEEL = '#5e615e', ROOF = '#7d7f78',
  SAND = '#b8a27a', SAND_DARK = '#a08c62', EARTH = '#7a6448', WIRE = '#4c4f4c', BAG = ['#a08c62', '#988458', '#ab976b'], WOOD = '#8a6a44', CRATE = '#6b6a3e', OLIVE = '#5c6146', DARK = '#2e302c';

const M = MODULE_GRID;
/** The cell's side and the storey's height; a wall's thickness; a post's square; a slab's thickness (model units). */
const C = M.cell * U, H = M.cell * U, T = M.wall * U, PO = M.post * U, SL = M.slab * U;
const DOOR_W = M.door.width * U, DOOR_H = SL + M.door.height * U, WIN0 = SL + M.window.from * U, WIN1 = SL + M.window.to * U, PARAPET = M.parapet * U;

function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return () => { a = (a + 0x6d2b79f5) >>> 0; let t = a; t = Math.imul(t ^ (t >>> 15), t | 1); t ^= t + Math.imul(t ^ (t >>> 7), t | 61); return ((t ^ (t >>> 14)) >>> 0) / 4294967296; };
}

// ---- the walls (a B-hut's: plywood sheathing on a stud frame, battens over the seams) -------------------------------------

/**
 * A panel's plates' depth: the bottom and top plates are the panel's foot and head, the sheathing runs between them (a
 * sheathing face flush with a plate's would z-fight: 67.65's z-fight pass, the owner: "z fighting issues with modular
 * construction, seemingly in window sills and edges of roofs"). The same rule at every trim: an opening's sheathing edge
 * stops inside its trim, a face meets another face only back to back.
 */
const PLATE = 0.14;
/** The panel's sheathing, plate to plate (`holes` cut through). */
const sheathing = (g: THREE.Group, holes: number[][] = []) => front(g, rect(-C / 2, PLATE, C / 2, H - PLATE), T, PLY, 0, 0.02, holes);
/** The panel's plates (bottom and top) and the battens over its seams on both faces. */
function frame(g: THREE.Group, battens: number[]): void {
  for (const y of [PLATE / 2, H - PLATE / 2]) box(g, C, PLATE, T + 0.04, FRAME, 0, y, 0);
  for (const x of battens) for (const s of [-1, 1]) box(g, 0.09, H - 0.28, 0.03, PLY_DARK, x, H / 2, s * (T / 2 + 0.015));
}
function wallPlain(g: THREE.Group): void {
  sheathing(g);
  frame(g, [-C / 4, 0, C / 4]);
  for (const s of [-1, 1]) box(g, C - 0.1, 0.08, 0.03, PLY_DARK, 0, H * 0.5, s * (T / 2 + 0.015));   // the mid rail
}
/** A door: the opening `DOOR_W` × `DOOR_H` between 0.4-person jambs, its trim, the leaf open against the wall, the team's band. */
function wallDoor(g: THREE.Group, p: UnitPalette): void {
  const dw = DOOR_W / 2, ow = dw + 0.03, oh = DOOR_H + 0.03;   // (the sheathing's opening: its edges inside the trims)
  front(g, [-C / 2, PLATE, -ow, PLATE, -ow, oh, ow, oh, ow, PLATE, C / 2, PLATE, C / 2, H - PLATE, -C / 2, H - PLATE], T, PLY, 0, 0.02);
  box(g, C, PLATE, T + 0.04, FRAME, 0, H - PLATE / 2, 0);
  for (const s of [-1, 1]) box(g, C / 2 - ow, PLATE, T + 0.04, FRAME, s * (C / 2 + ow) / 2, PLATE / 2, 0);   // the bottom plate either side of the opening
  for (const x of [-dw, dw]) box(g, 0.09, DOOR_H, T + 0.06, FRAME, x + Math.sign(x) * 0.045, DOOR_H / 2, 0);
  box(g, DOOR_W + 0.18, 0.09, T + 0.06, FRAME, 0, DOOR_H + 0.045, 0);
  const leaf = group(g, -dw, 0, T / 2 + 0.03); leaf.rotation.y = -1.75;   // open, swung out
  front(leaf, rect(0.02, 0.03, DOOR_W - 0.02, DOOR_H - 0.03), 0.06, PLY_DARK, 0, 0.012);
  box(leaf, DOOR_W - 0.3, 0.16, 0.07, p.body, DOOR_W / 2, 2.0, 0);
  box(leaf, 0.06, 0.06, 0.1, STEEL, DOOR_W - 0.16, 1.25, 0);
}
/** A window: the opening a person wide, 0.70–1.5 people over the floor (69.16; 326), its sill, the shutter propped open over it. */
function wallWindow(g: THREE.Group): void {
  const w = U / 2;
  sheathing(g, [rev(rect(-w, WIN0 - 0.03, w, WIN1))]);   // (the opening's foot inside the sill)
  frame(g, [-C / 2 + 0.45, C / 2 - 0.45]);
  box(g, U + 0.25, 0.08, T + 0.18, FRAME, 0, WIN0 - 0.04, 0.05);   // the sill
  const shutter = group(g, 0, WIN1 + 0.04, T / 2 + 0.02); shutter.rotation.x = -1.3;   // (an awning: propped near flat, the opening's sight lines clear under it)
  front(shutter, rect(-w - 0.06, -(WIN1 - WIN0) - 0.06, w + 0.06, 0), 0.05, PLY_DARK, 0, 0.012);
  box(g, 0.035, 0.95, 0.035, STEEL, w - 0.1, WIN1 - 0.12, 0.5).rotation.x = 1.0;   // its prop
}
/**
 * A firing slit at the sim's opening (constants/modules.ts MODULE_SHAPES wallSlit: 1 × 0.3 people, 1.1–1.4 over the foot;
 * it was drawn 0.6 × 0.14 at 0.87–1.01, wall where rounds pass), steel-framed outside; inside, the sim's step under it (a
 * person tall, 0.33 deep): a ply bench, sandbags along its top within its height. The bench spans the opening and stops
 * short of the corners (`SLIT_BENCH`: two slit walls' benches meeting in a corner overlapped), its foot in the floor's deck.
 */
const SLIT_BENCH = 0.5 * U + 0.05;
function wallSlit(g: THREE.Group): void {
  const s0 = 1.1 * U, s1 = 1.4 * U, w = 0.5 * U;
  sheathing(g, [rev(rect(-w, s0, w, s1))]);
  frame(g, [-C / 2 + 0.3, -w - 0.25, w + 0.25, C / 2 - 0.3]);   // (the battens clear of the opening's frame)
  front(g, [-w - 0.12, s0 - 0.12, w + 0.12, s0 - 0.12, w + 0.12, s1 + 0.12, -w - 0.12, s1 + 0.12], 0.04, STEEL, T / 2 + 0.02, 0.008, [rev(rect(-w, s0, w, s1))]);
  const top = U, z0 = -0.12 * U, z1 = -0.45 * U, bag = REFERENCE.sandbag.height, b = SLIT_BENCH;   // (the step's top, its depth from the wall's middle)
  front(g, rect(-b, SL - 0.05, b, top - bag - 0.05), z0 - z1, PLY_DARK, (z0 + z1) / 2, 0.01);   // the bench (its foot in the deck)
  box(g, 2 * b, 0.05, z0 - z1, FRAME, 0, top - bag - 0.025, (z0 + z1) / 2);                       // its top board
  for (const x of [-0.62, 0, 0.62]) sandbag(g, x, top - bag, (z0 + z1) / 2, x === 0 ? 1 : 0, 0.6, false);   // bags on it, under the step's top
}
/**
 * A post at a grid node: square, a storey tall, capped; the T-join's carries a cleat on its third face. The cap rides the
 * storey's top, so the next storey's post starts inside it (one storey's post running on past H showed its faces flush with
 * the next one's).
 */
function post(g: THREE.Group, t: boolean): void {
  slab(g, rect(-PO / 2, 0, PO / 2, H), PO, FRAME, 0, 0.025);
  box(g, PO + 0.05, 0.07, PO + 0.05, PLY_DARK, 0, H, 0);
  if (t) box(g, 0.14, H - 0.24, 0.07, FRAME, 0, H / 2, PO / 2 + 0.035);
}

// ---- floors, roofs, stairs, the gate ---------------------------------------------------------------------------------------

/** A floor tile: a plywood deck on its joists (the slab, 0.15 people), its sheet seams. The edge joists stand in from the walls' plates (a joist's face flush with a wall's showed under an upper floor). */
function floor(g: THREE.Group): void {
  top(g, rect(-C / 2, -C / 2, C / 2, C / 2), 0.09, PLY, SL - 0.09, 0.012);
  for (const z of [-C / 2 + 0.3, 0, C / 2 - 0.3]) box(g, C - 0.12, SL - 0.09, 0.12, FRAME, 0, (SL - 0.09) / 2, z);   // the joists
  box(g, C - 0.12, 0.005, 0.03, PLY_DARK, 0, SL + 0.002, 0);
  box(g, 0.03, 0.005, C - 0.12, PLY_DARK, 0, SL + 0.002, 0);
}
/**
 * A roof slab at the storey's top: corrugated metal on purlins over a ply deck (the next storey's floor where one stands on it).
 * Neighbouring slabs meet edge to edge: the sheet and its corrugation stop at the cell's edge, and the eave is a drip turned
 * down on each side — over a neighbour it lies under the neighbour's sheet, never in its plane (overlapping sheets z-fought).
 * The deck starts a little under the storey's top, so a wall standing on the slab (a storey's plate) never shares its underside.
 */
function roof(g: THREE.Group): void {
  top(g, rect(-C / 2, -C / 2, C / 2, C / 2), 0.07, PLY_DARK, H - 0.01, 0.01);
  for (const x of [-C / 2 + 0.25, 0, C / 2 - 0.25]) box(g, 0.12, 0.12, C - 0.02, FRAME, x, H + 0.12, 0);   // the purlins (their ends short of the drips' ends)
  const y1 = H + SL - 0.02, e = T / 2 + 0.08, drop = 0.08, th = 0.04;   // the sheet's top; the drip's reach (past the wall's face) and fall
  top(g, rect(-C / 2, -C / 2, C / 2, C / 2), th, ROOF, y1 - th);
  const drip = [C / 2, y1 - th, C / 2 + e, y1 - th - drop, C / 2 + e, y1 - drop, C / 2, y1];
  for (let q = 0; q < 4; q++) group(g).rotation.y = q * Math.PI / 2;
  for (const side of g.children.slice(-4)) front(side, drip, C, ROOF);   // the drips, one a side (each a cell long: two in line never overlap)
  for (let k = 0; k < 11; k++) box(g, 0.06, 0.04, C, ROOF, -C / 2 + 0.12 + k * ((C - 0.24) / 10), H + SL, 0);   // the corrugation (its end ribs clear of a wall's face on the slab)
}
/** Stairs: one cell, a storey up along +z (a 2-person run over a 2-person rise, 45°), 1.33 people wide, stringers, a rail. */
function stairs(g: THREE.Group): void {
  const n = 12, rise = H / n, run = C / n, w = 2.4;
  for (let i = 0; i < n; i++) box(g, w, 0.07, run + 0.05, PLY, 0, SL + (i + 1) * rise - 0.035, -C / 2 + (i + 0.5) * run);
  for (const sx of [-1, 1]) slab(g, [-C / 2, 0, -C / 2 + 0.35, 0, C / 2, H + SL - 0.35, C / 2, H + SL, -C / 2, SL + 0.35], 0.07, FRAME, sx * (w / 2 + 0.035), 0.01);
  for (const z of [-C / 2 + 0.2, C / 2 - 0.2]) box(g, 0.05, 1.0, 0.05, STEEL, w / 2 + 0.06, SL + (z + C / 2) / C * H + 0.5, z);
  const rail = new THREE.Mesh(new THREE.BoxGeometry(0.05, 0.05, Math.hypot(C, H)), mat(STEEL));
  rail.position.set(w / 2 + 0.06, SL + H / 2 + 1.0, 0); rail.rotation.x = -Math.atan2(H, C); g.add(rail);
}
/** A gate: a door panel's span without the lintel — concrete jambs a storey tall either side of the 1.2-person opening, the hinges' pins on them; its two leaves are `gateLeaf`s on the hinges (`GATE_LEAVES`). */
function gate(g: THREE.Group): void {
  const dw = DOOR_W / 2, jamb = (C - DOOR_W) / 2;
  for (const s of [-1, 1]) {
    slab(g, rect(-T, 0, T, H), jamb - 0.02, CONCRETE, s * (dw + jamb / 2), 0.04);                // the jamb
    for (const y of [0.35, M.door.height * U - 0.2]) box(g, 0.1, 0.16, 0.12, STEEL, s * (dw - 0.02), y, 0);   // the hinges
  }
}
/**
 * A gate's leaf, built on its pivot: the hinge at the origin, the leaf running along −x (`length`) and `height` tall, a
 * sheet-steel panel on a frame (solid: a shut gate stops the eye as the sim's leaf shape does), the team's band on both
 * faces. The gate's other leaf is this one turned half round (`GATE_LEAVES`); the root is the pivot (named), so a turn of the
 * instance's yaw about it swings the leaf. Destroyed: torn off its hinges, lying flat.
 */
function gateLeaf(g: THREE.Group, p: UnitPalette, length: number, form: ModuleForm): void {
  g.name = 'leafPivot';
  const leaf = group(g, 0, 0, 0), h = M.door.height * U, t = 0.06;
  if (form === 'destroyed') { leaf.rotation.set(Math.PI / 2, 0.25, 0); leaf.position.set(-0.3, 0.1, -0.15); }
  front(leaf, rect(-length + 0.04, 0.14, -0.04, h), t, STEEL, 0, 0.01);                                     // the sheet (its foot inside the bottom rail)
  for (const x of [-0.05, -length + 0.05]) box(leaf, 0.1, h - 0.06, 0.1, DARK, x, h / 2 + 0.06, 0);          // the stiles
  for (const y of [0.16, h / 2, h]) box(leaf, length, 0.08, 0.1, DARK, -length / 2, y, 0);                  // the rails
  for (const sz of [-1, 1]) box(leaf, length * 0.6, 0.22, 0.02, p.body, -length / 2, h * 0.68, sz * (t / 2 + 0.01));   // the band
  box(leaf, 0.05, 0.3, 0.16, STEEL, -length + 0.12, h * 0.45, 0);                                           // the latch
}
/**
 * A vehicle gate, two cells (lane F's span class): concrete jambs a storey tall either side of the 3.2-person opening, no
 * lintel (the walker's 3.4 and a lorry's load pass under nothing), the hinges' pins on them; its two leaves are
 * `gateWideLeaf`s on the hinges (`GATE_LEAVES`); a drop-arm barrier outside (−z), raised on its post.
 */
function gateWide(g: THREE.Group, p: UnitPalette): void {
  const half = M.vehicleGate.width * U / 2, jamb = C - half;
  for (const s of [-1, 1]) {
    slab(g, rect(-T, 0, T, H - 0.06), jamb - 0.02, CONCRETE, s * (half + jamb / 2), 0.04);         // the jamb (its top inside the cap)
    box(g, jamb + 0.04, 0.12, 2 * T + 0.1, CONCRETE_DARK, s * (half + jamb / 2), H - 0.06, 0);      // its cap
    for (const y of [0.35, M.door.height * U - 0.2]) box(g, 0.12, 0.18, 0.14, STEEL, s * (half - 0.02), y, 0);   // the hinges
  }
  const bx = -half + 0.2, bz = -T - 0.7;
  slab(g, rect(bz - 0.12, 0, bz + 0.12, 1.15), 0.24, STEEL, bx, 0.02);                               // the barrier's post
  const arm = group(g, bx, 1.05, bz); arm.rotation.z = Math.PI - 0.12;                               // its arm, raised (down along +x it bars the way)
  for (let k = 0; k < 6; k++) box(arm, 0.11, (2 * half - 0.6) / 6, 0.11, k % 2 ? '#d8d4c8' : p.body, 0, -(k + 0.5) * (2 * half - 0.6) / 6, 0);
}
/**
 * A ladder (lane F's seat: a tower's deck or a wall-walk reached without a stair cell): steel stiles 0.4 people apart, leaning
 * at 75° from its foot (−z) to a deck's edge a storey and a slab up (its top at z = 0), the stiles running on past the edge for
 * the hands, rungs a foot apart. Its foot stands about 0.6 people out: half a cell, where the stairs take a whole one.
 */
function ladder(g: THREE.Group): void {
  const up = H + SL, run = up / Math.tan(75 * Math.PI / 180), w = M.ladder.width * U, len = Math.hypot(up, run), ext = 0.9;
  const l = group(g, 0, 0, -run); l.rotation.x = Math.atan2(run, up);   // (leant: its top toward +z)
  for (const s of [-1, 1]) {
    box(l, 0.06, len + ext, 0.07, STEEL, s * w / 2, (len + ext) / 2, 0);
    box(l, 0.12, 0.04, 0.16, DARK, s * w / 2, 0.02, 0);                // its foot
  }
  for (let y = 0.3; y < len - 0.05; y += 0.3) box(l, w, 0.035, 0.035, STEEL, 0, y, 0);
}
/**
 * A plinth (§5.22's slope rule: each column of cells seated on its own `baseLift`): one cell square, its top at y = 0 (where
 * the column's floor or wall foot stands), a person deep, built to stretch — a placement's `stretch` scales its depth to the
 * terrain under it and a little into it. Rammed earth behind a revetment of vertical boards on corner stakes, nothing across
 * (a stretch draws every part true), no openings.
 */
function plinth(g: THREE.Group): void {
  const d = U, h = C / 2;
  slab(g, rect(-h, -d, h, 0), C, EARTH, 0, 0.02);
  for (const s of [-1, 1]) {
    for (let k = 0; k < 8; k++) {
      const a = -h + (k + 0.5) * (C / 8);
      box(g, C / 8 - 0.05, d, 0.05, k % 2 ? WOOD : FRAME, a, -d / 2, s * (h + 0.025));      // the boards on ±z
      box(g, 0.05, d, C / 8 - 0.05, k % 2 ? FRAME : WOOD, s * (h + 0.025), -d / 2, a);      // and on ±x
    }
    for (const sz of [-1, 1]) box(g, 0.14, d + 0.04, 0.14, DARK, s * (h + 0.04), -d / 2 + 0.02, sz * (h + 0.04));   // the stakes (proud of the boards)
  }
}

// ---- the defences ------------------------------------------------------------------------------------------------------------

/**
 * One filled sandbag, pillowed (a chamfered block), its long side (`len`, a whole bag's by default) along x; `across` turns it
 * a quarter (a slit's shelf lays them across); `width` across it. (The parapet's bags lay across it until the corners: a turn
 * too many, which left a run's courses open between the bags and at its joints.)
 */
function sandbag(g: THREE.Object3D, x: number, y: number, z: number, i: number, len = BAG_ALONG, across = false, width = REFERENCE.sandbag.width): THREE.Mesh {
  const b = REFERENCE.sandbag;
  const m = slab(g, rect(-width / 2, 0, width / 2, b.height), len, BAG[i % BAG.length], 0, b.height * 0.32);
  m.position.set(x, y, z); if (across) m.rotation.y = Math.PI / 2;
  return m;
}
/** Concrete T-walls: two sections a cell, each a person wide and a storey tall (nobody shoots over it) on its T's foot, lifting loops. */
function tWall(g: THREE.Group, form: ModuleForm, r: () => number): void {
  for (const s of [-1, 1]) {
    const sec = group(g, s * C / 4, 0, 0);
    slab(sec, [-0.65, 0, 0.65, 0, 0.65, 0.38, 0.17, 0.55, 0.14, H, -0.14, H, -0.17, 0.55, -0.65, 0.38], C / 2 - 0.05, CONCRETE, 0, 0.035);
    for (const x of [-0.5, 0.5]) { const loop = new THREE.Mesh(new THREE.TorusGeometry(0.08, 0.018, 4, 8, Math.PI), mat(STEEL)); loop.position.set(x, H, 0); sec.add(loop); }
    if (form !== 'whole' && s < 0) { const bite = box(sec, 0.55, 0.45, 0.34, CONCRETE_DARK, (r() - 0.5) * 0.6, H - 0.25, 0); bite.rotation.z = 0.4; }
  }
}
/** Hesco: two wire-mesh bastions a cell, each a person square, filled to a parapet's height; the frame's posts and top rails. */
function hesco(g: THREE.Group, form: ModuleForm, r: () => number): void {
  const n = 2, cell = C / n, h = PARAPET, size = U;
  for (let i = 0; i < n; i++) {
    const x = -C / 2 + (i + 0.5) * cell;
    const gone = form === 'destroyed' && r() < 0.5, slump = form !== 'whole' && !gone && r() < (form === 'destroyed' ? 0.8 : 0.4);
    if (gone) { front(g, rect(x - cell / 2, 0, x + cell / 2, 0.3), size * 1.35, SAND_DARK, 0, 0.1); continue; }   // its fill spilled where it stood
    // (the dark fill settled 3 cm lower: two runs' bastions overlapping at a corner never share a top; 63.14: a destroyed one's burst to the sim's heap)
    const hh = slump ? (form === 'destroyed' ? heapTop() * 0.8 : h * 0.6) : h;
    const fill = front(g, rect(x - cell / 2 + 0.03, 0, x + cell / 2 - 0.03, hh - (i % 2 ? 0.08 : 0.05)), size - 0.07, i % 2 ? SAND_DARK : SAND, 0, 0.05);
    if (slump) fill.rotation.x = 0.1;
    for (const sx of [-1, 0, 1]) for (const sz of [-1, 1]) box(g, 0.04, hh, 0.04, WIRE, x + sx * (cell / 2 - 0.03), hh / 2, sz * (size / 2 - 0.02));
    for (const sz of [-1, 1]) {   // the mesh's grid on both faces, in quarters
      for (const f of [0.25, 0.5, 0.75]) box(g, cell - 0.06, 0.025, 0.025, WIRE, x, hh * f, sz * (size / 2 - 0.01));
      for (const f of [-0.25, 0.25]) box(g, 0.025, hh, 0.025, WIRE, x + f * cell, hh / 2, sz * (size / 2 - 0.01));
    }
  }
  if (form === 'whole') for (const sz of [-1, 1]) box(g, C, 0.035, 0.035, WIRE, 0, h - 0.02, sz * (size / 2 - 0.02));
}
/** A sandbag's length; a course's height; the parapet's depth below (two bags), and a corner's side (a bag's length and width). */
const BAG_ALONG = REFERENCE.sandbag.depth * 1.15, BAG_COURSE = REFERENCE.sandbag.height * 0.9, BAG_CORNER = BAG_ALONG + REFERENCE.sandbag.width;
/**
 * The parapet's courses: six, a person tall; the lower three two bags deep (`bagRows`: the rows' z), the rest one. A course
 * settles a little into the one under it; every other course's bags are 3 cm narrower (`bagWidth`), so two courses' faces
 * never share that band.
 */
const bagWidth = (row: number) => REFERENCE.sandbag.width - (row % 2 ? 0.03 : 0);
const bagCourses = (form: ModuleForm) => form === 'destroyed' ? 2 : Math.round(PARAPET / BAG_COURSE);
const bagRows = (row: number) => row < 3 ? [-REFERENCE.sandbag.width / 2 - 0.01, REFERENCE.sandbag.width / 2 + 0.01] : [0];
/**
 * A sandbag parapet a person tall: six courses of bags laid along it, staggered (every other course half a bag on, half bags at
 * its ends), two bags deep for the lower three. It stops half a corner short of each node, where a `sandbagCorner` stands at
 * a joint: two runs meeting at a corner both reached its square and z-fought there, and stopping one short left a hole.
 */
function sandbags(g: THREE.Group, form: ModuleForm, r: () => number): void {
  const L = C - BAG_CORNER, per = Math.round(L / BAG_ALONG), step = L / per, gap = Math.max(0, step - BAG_ALONG), rows = bagCourses(form);
  const half = step - BAG_ALONG / 2 - 1.5 * gap;   // (an end's half bag in a staggered course)
  let i = 0;
  for (let row = 0; row < rows; row++) for (const z of bagRows(row)) {
    const bags: Array<[number, number]> = row % 2
      ? [[-L / 2 + gap / 2 + half / 2, half], ...Array.from({ length: per - 1 }, (_, k): [number, number] => [-L / 2 + (k + 1) * step, BAG_ALONG]), [L / 2 - gap / 2 - half / 2, half]]
      : Array.from({ length: per }, (_, k): [number, number] => [-L / 2 + (k + 0.5) * step, BAG_ALONG]);
    for (const [x, len] of bags) {
      if (form === 'damaged' && row >= rows - 2 && r() < 0.35) continue;
      sandbag(g, x, row * BAG_COURSE, z, i++, len, false, bagWidth(row));
    }
  }
  if (form === 'destroyed') for (let k = 0; k < 7; k++) { const s = sandbag(g, (r() - 0.5) * L, 0, (r() < 0.5 ? -1 : 1) * (0.7 + r() * 0.5), k); s.rotation.y = r() * 3; }   // the top courses spilled
}
/**
 * A sandbag corner at a node (placed like a post, `atNode`): a pier of the parapet's courses, a bag's length and width a side,
 * each course four bags round the node in a pinwheel turned the other way from the one under it, so the courses bind as a
 * laid corner's do; the runs stop at its faces. It closes any joint of runs (a corner, a T, a cross, in line) and a run's end
 * against a T-wall or a Hesco.
 */
function sandbagCorner(g: THREE.Group, form: ModuleForm, r: () => number): void {
  const rows = bagCourses(form), q = BAG_CORNER / 2, w = REFERENCE.sandbag.width;
  let i = 1;
  for (let row = 0; row < rows; row++) for (let k = 0; k < 4; k++) {
    if (form === 'damaged' && row >= rows - 2 && r() < 0.35) continue;
    const side = group(g, 0, row * BAG_COURSE, 0); side.rotation.y = k * Math.PI / 2;
    sandbag(side, (row % 2 ? 1 : -1) * (q - BAG_ALONG / 2), 0, q - w / 2, i++, BAG_ALONG, false, bagWidth(row));
  }
  box(g, BAG_ALONG - w - 0.02, (rows - 1) * BAG_COURSE + 0.25, BAG_ALONG - w - 0.02, SAND_DARK, 0, ((rows - 1) * BAG_COURSE + 0.25) / 2, 0);   // the fill in the pinwheel's eye, under its top
}
/** Triple concertina wire: two coils on the ground, one on them, on pickets — thin. */
function wire(g: THREE.Group, form: ModuleForm): void {
  const coils: Array<[number, number]> = form === 'destroyed' ? [[0.45, -0.45]] : form === 'damaged' ? [[0.45, -0.45], [0.45, 0.45]] : [[0.45, -0.45], [0.45, 0.45], [1.2, 0]];
  for (const [y, z] of coils) for (let k = 0; k < 8; k++) {
    const ring = new THREE.Mesh(new THREE.TorusGeometry(0.45, 0.012, 3, 8), mat(WIRE));
    ring.position.set(-C / 2 + 0.22 + k * ((C - 0.44) / 7), y, z); ring.rotation.y = Math.PI / 2 + 0.3; g.add(ring);
  }
  for (const x of [-C / 2 + 0.1, C / 2 - 0.1]) for (const z of [-0.6, 0.6]) box(g, 0.04, form === 'whole' ? 1.6 : 0.9, 0.04, STEEL, x, form === 'whole' ? 0.8 : 0.45, z);
}

// ---- storage ---------------------------------------------------------------------------------------------------------------

/** Steel shelving against a wall: a person wide, four shelves on four uprights, boxes and cases on them. */
function shelving(g: THREE.Object3D, r: () => number): void {
  const w = U, d = 0.6, h = 2.2;
  for (const sx of [-1, 1]) for (const sz of [-1, 1]) box(g, 0.045, h, 0.045, STEEL, sx * (w / 2 - 0.02), h / 2, sz * (d / 2 - 0.02));
  for (let k = 0; k < 4; k++) {
    const y = 0.18 + k * 0.62;
    box(g, w, 0.035, d, STEEL, 0, y, 0);
    for (let x = -w / 2 + 0.08; x < w / 2 - 0.3;) { const bw = 0.25 + r() * 0.3, bh = 0.18 + r() * 0.28; if (r() < 0.8) box(g, bw, bh, d * 0.8, r() < 0.5 ? CRATE : OLIVE, x + bw / 2, y + 0.018 + bh / 2, 0); x += bw + 0.05; }
  }
}
/** A storage crate a person a side (the dump's stock, four to a cell): a plywood box on skids, its battens, a stencil band. */
function crate(g: THREE.Group, p: UnitPalette): void {
  const s = M.crate * U;
  for (const x of [-s / 2 + 0.15, s / 2 - 0.15]) box(g, 0.14, 0.12, s, WOOD, x, 0.06, 0);   // the skids
  slab(g, rect(-s / 2 + 0.03, 0.12, s / 2 - 0.03, s - 0.03), s - 0.06, CRATE, 0, 0.03);
  for (const y of [0.2, s / 2, s - 0.1]) for (const sz of [-1, 1]) box(g, s - 0.02, 0.1, 0.03, WOOD, 0, y, sz * (s / 2 - 0.02));
  for (const y of [0.2, s / 2, s - 0.1]) for (const sx of [-1, 1]) box(g, 0.03, 0.1, s - 0.02, WOOD, sx * (s / 2 - 0.02), y, 0);
  for (const sz of [-1, 1]) box(g, s * 0.6, 0.16, 0.035, p.body, 0, s * 0.72, sz * (s / 2 - 0.005));
}
/**
 * A container fitted out as a room, a cell wide and two long, a storey tall: a 20-footer's corrugated steel, its doors open at
 * one end, shelving inside down one side. Built long along z, its doors at −z; `buildModule` turns it to the span class's
 * run (two cells along x, its doors at −x). (A cheat on the real 2.44 × 2.59: grown to the grid so a person stands in it under the
 * camera's 2-person ceiling.)
 */
function containerRoom(g: THREE.Group, p: UnitPalette): void {
  const L = 2 * C - 0.2, Wd = C - 0.2, Hh = H - 0.03, t = 0.08;   // (a little under the storey: its roof never flush with a wall's top beside it)
  slab(g, rect(-L / 2, 0, L / 2, SL), Wd, DARK, 0, 0.02);                                               // the floor
  slab(g, rect(-L / 2, Hh - t, L / 2, Hh), Wd, OLIVE, 0, 0.02);                                         // the roof
  for (const s of [-1, 1]) {
    slab(g, rect(-L / 2, SL, L / 2, Hh - t), t, OLIVE, s * (Wd / 2 - t / 2), 0.01);                    // the sides
    for (let k = 0; k < 14; k++) box(g, 0.05, Hh - 0.4, 0.14, OLIVE, s * (Wd / 2 + 0.015), Hh / 2, -L / 2 + 0.4 + k * ((L - 0.8) / 13));   // the corrugation
    box(g, 0.06, 0.16, L, DARK, s * (Wd / 2 - 0.02), 0.08, 0);                                          // the bottom rail
    const door = group(g, s * (Wd / 2), 0, -L / 2); door.rotation.y = s * 1.9;                          // the doors, open
    front(door, rect(Math.min(0, -s * (Wd / 2 - 0.03)), 0.1, Math.max(0, -s * (Wd / 2 - 0.03)), Hh - 0.1), 0.06, OLIVE, 0, 0.012);
    for (const xx of [0.25, 0.6]) box(door, 0.05, Hh - 0.4, 0.05, STEEL, -s * xx, Hh / 2, -0.05);      // its locking bars
  }
  for (const sx of [-1, 1]) for (const sy of [0, 1]) for (const sz of [-1, 1]) box(g, 0.21, 0.21, 0.21, DARK, sx * (Wd / 2 - 0.075), sy ? Hh - 0.095 : 0.105, sz * (L / 2 - 0.095));   // corner castings, proud of the box
  front(g, rect(-Wd / 2, SL, Wd / 2, Hh - t), t, OLIVE, L / 2 - t / 2, 0.01);                          // the far end
  front(g, rect(-Wd / 2 + 0.1, Hh - 0.65, Wd / 2 - 0.1, Hh - 0.35), 0.03, p.body, L / 2 + 0.015);      // the team's band
  for (const z of [-1.2, 0.8, 2.8]) { const sh = group(g, Wd / 2 - 0.42, SL, z); sh.rotation.y = -Math.PI / 2; shelving(sh, mulberry32(7 + Math.round(z * 10))); }
}
/** A guard tower: four braced legs, its deck a storey up (one cell), a plywood parapet on three sides (the stairs come up the fourth, −z), a roof on posts. */
function tower(g: THREE.Group, form: ModuleForm, r: () => number): void {
  const S = C, up = H;
  for (const sx of [-1, 1]) for (const sz of [-1, 1]) slab(g, rect(sz * (S / 2 - 0.12) - 0.12, 0, sz * (S / 2 - 0.12) + 0.12, up), 0.24, FRAME, sx * (S / 2 - 0.12), 0.02);
  for (const sx of [-1, 1]) { const b = box(g, 0.08, Math.hypot(S, up * 0.6) - 0.2, 0.07, FRAME, sx * (S / 2 - 0.12), up * 0.45, 0); b.rotation.x = Math.atan2(S, up * 0.6); }
  for (const sz of [-1, 1]) { const b = box(g, 0.08, Math.hypot(S, up * 0.6) - 0.2, 0.07, FRAME, 0, up * 0.45, sz * (S / 2 - 0.12)); b.rotation.z = Math.atan2(S, up * 0.6); }
  top(g, rect(-S / 2, -S / 2, S / 2, S / 2), SL, PLY, up, 0.015);
  const deck = up + SL;
  const sides: Array<[number, number, number]> = [[0, S / 2, 0], [S / 2, 0, Math.PI / 2], [-S / 2, 0, Math.PI / 2]];
  for (const [x, z, ry] of sides) {
    if (form === 'destroyed' && r() < 0.6) continue;
    const e = group(g, x, deck, z); e.rotation.y = ry;
    front(e, rect(-S / 2, 0, S / 2, PARAPET * 0.72), 0.12, PLY, 0, 0.012);
    for (const xx of [-S / 4, 0, S / 4]) box(e, 0.08, PARAPET * 0.66, 0.03, PLY_DARK, xx, PARAPET * 0.36, 0.08);
  }
  if (form !== 'destroyed') {
    for (const sx of [-1, 1]) for (const sz of [-1, 1]) box(g, 0.1, U * 1.15, 0.1, FRAME, sx * (S / 2 - 0.06), deck + U * 0.575, sz * (S / 2 - 0.06));
    const rf = top(g, rect(-S / 2 - 0.25, -S / 2 - 0.25, S / 2 + 0.25, S / 2 + 0.25), 0.06, ROOF, deck + U * 1.15, 0.01);
    if (form === 'damaged') { rf.rotation.z = 0.09; rf.position.y = -0.2; }
  }
}

// ---- the kit -------------------------------------------------------------------------------------------------------------

const Y = new THREE.Vector3(0, 1, 0), QUARTER = new THREE.Quaternion().setFromAxisAngle(Y, Math.PI / 2);
/** The modules with a wreck of their own (spilled, burst, cut); the rest take the generic one (63.14: the T-wall's and the tower's too — a toppled section, the legs and deck, stood a person and more). */
const OWN_WRECK = new Set<BaseModule>(['hesco', 'sandbags', 'sandbagCorner', 'wire', 'gateLeaf', 'gateWideLeaf']);
/**
 * 63.14 (lane H's 72.1 found it): the sim's heap's top (model units) — a module of a rubble kind at 0 is a heap a person steps
 * over and sees and fires across (constants/modules.ts RUBBLE, under the step-over: a breach), drawn so. Read at use.
 */
const heapTop = (): number => RUBBLE.heightPeople * U;
/** A rubble kind's destroyed form no taller than the sim's heap: whatever still stands over it squashed down to it, about the slab (y 0). */
function downToHeap(g: THREE.Group): void {
  g.updateMatrixWorld(true);
  const top = new THREE.Box3().setFromObject(g, true).max.y;
  if (top <= heapTop()) return;
  const inner = new THREE.Group();
  inner.scale.y = heapTop() / top;
  for (const c of [...g.children]) inner.add(c);
  g.add(inner);
}
/**
 * A module in a form, built at the origin (model units). Damaged: 63.4's damaged pass (scorches, small parts knocked off) and
 * the module's own where it has one (a slumped bastion, a bitten wall); destroyed: its own wreck where it has one, else the
 * damaged pass at full and the module cut down to a jagged stub, a heap of its stuff at its foot — a rubble kind's no taller
 * than the sim's heap (63.14).
 */
export function buildModule(kind: BaseModule, p: UnitPalette, form: ModuleForm = 'whole', seed = 1): THREE.Group {
  const g = new THREE.Group(), r = mulberry32(seed * 977 + BASE_MODULES.indexOf(kind) * 131 + MODULE_FORMS.indexOf(form));
  switch (kind) {
    case 'wallPlain': wallPlain(g); break;
    case 'wallDoor': wallDoor(g, p); break;
    case 'wallWindow': wallWindow(g); break;
    case 'wallSlit': wallSlit(g); break;
    case 'post': post(g, false); break;
    case 'postT': post(g, true); break;
    case 'floor': floor(g); break;
    case 'roof': roof(g); break;
    case 'stairs': stairs(g); break;
    case 'gate': gate(g); break;
    case 'tWall': tWall(g, form, r); break;
    case 'hesco': hesco(g, form, r); break;
    case 'sandbags': sandbags(g, form, r); break;
    case 'sandbagCorner': sandbagCorner(g, form, r); break;
    case 'wire': wire(g, form); break;
    case 'shelving': shelving(g, r); break;
    case 'crate': crate(g, p); break;
    case 'containerRoom': containerRoom(g, p); for (const c of g.children) { c.position.applyAxisAngle(Y, Math.PI / 2); c.quaternion.premultiply(QUARTER); } break;
    case 'tower': tower(g, form, r); break;
    case 'ladder': ladder(g); break;
    case 'gateWide': gateWide(g, p); break;
    case 'plinth': plinth(g); break;
    case 'gateLeaf': gateLeaf(g, p, DOOR_W / 2, form); break;
    case 'gateWideLeaf': gateLeaf(g, p, M.vehicleGate.width * U / 2, form); break;
  }
  if (form === 'damaged') damageModel(g, seed + 3, 0.6, kind === 'stairs' ? [...g.children] : []);   // (the stairs keep their treads)
  if (form === 'destroyed') { if (OWN_WRECK.has(kind)) damageModel(g, seed + 5, 1); else wreck(g, kind, r, seed); }
  if (form === 'destroyed' && RUBBLE_KINDS.has(kind)) downToHeap(g);   // (63.14: the drawing at the sim's heap)
  return g;
}
/** A module wrecked: scorched, cut down to a stub (what stood above it came down), a heap of its pieces at its foot. */
function wreck(g: THREE.Group, kind: BaseModule, r: () => number, seed: number): void {
  damageModel(g, seed + 5, 1);
  if (kind === 'floor') { for (const c of g.children) c.rotation.z += (r() - 0.5) * 0.06; return; }   // (a deck stays a deck: scorched, sagged)
  if (kind === 'plinth') { for (const c of g.children) c.rotation.z += (r() - 0.5) * 0.08; return; }  // (earth stays: the boards burst)
  g.updateMatrixWorld(true);
  const all = new THREE.Box3().setFromObject(g, true);
  // (a rubble kind's stub at the sim's heap, 63.14 — it stood at 30–45 % of the module, 1.3 people for a wall; the rest as was)
  const cut = kind === 'roof' ? all.min.y + 0.05 : RUBBLE_KINDS.has(kind) ? heapTop() * (0.6 + r() * 0.15) : all.min.y + (all.max.y - all.min.y) * (0.3 + r() * 0.15);
  for (const c of [...g.children]) {
    const b = new THREE.Box3().setFromObject(c, true);
    if (b.min.y > cut) g.remove(c);
    else if (b.max.y > cut) {
      c.scale.y *= Math.max(0.12, (cut - b.min.y) / Math.max(0.01, b.max.y - b.min.y)) * (0.75 + r() * 0.4); c.rotation.z += (r() - 0.5) * 0.12;
      c.position.y += b.min.y - new THREE.Box3().setFromObject(c, true).min.y;   // (63.14: kept on its foot — scaled about its middle, a batten floated at chest height)
    }
  }
  const heap = kind === 'roof' ? ROOF : kind === 'tWall' ? CONCRETE_DARK : kind === 'gate' || kind === 'gateWide' || kind === 'ladder' || kind === 'shelving' ? STEEL : kind === 'crate' ? CRATE : kind === 'containerRoom' ? OLIVE : PLY_DARK;
  const sx = Math.max(0.8, all.max.x - all.min.x), sz = Math.max(1.2, all.max.z - all.min.z);
  for (let i = 0; i < 8; i++) {
    const b = box(g, 0.3 + r() * 0.6, 0.06 + r() * 0.14, 0.2 + r() * 0.5, i % 3 ? heap : DARK, (r() - 0.5) * sx, 0.06, (r() - 0.5) * sz); b.rotation.set((r() - 0.5) * 0.3, r() * 3, (r() - 0.5) * 0.3);
    b.updateMatrixWorld(true); b.position.y -= Math.min(0, new THREE.Box3().setFromObject(b, true).min.y + 0.02);   // (tipped, a long piece's end sank under the ground)
  }
}

// ---- 72.5: masonry, for the settlements' houses (DECISIONS 323, 325; design_sheets/masonry.html) -----------------------------

/**
 * The masonry looks (325: several, one a settlement, keyed by the planet — rendering/moduleLook.ts settlementLook): lime render
 * over block, fieldstone, brick, adobe. A look draws a layout's walls, posts (its piers) and roofs; the floors, stairs and
 * furniture stay the team kit's. Presentation only: the sim's shapes are the kit's, so every opening is the sim's (constants/
 * modules.ts MODULE_SHAPES), drawn open where the sim is open.
 */
export type MasonryLook = 'render' | 'fieldstone' | 'brick' | 'adobe';
export const MASONRY_LOOKS: readonly MasonryLook[] = ['render', 'fieldstone', 'brick', 'adobe'];
interface MasonryDef {
  face: string; core: string; trim: string; quoin?: string; base?: string; patch?: string;
  roof: 'flat' | 'pitched'; roofTop: string; tiles: string; rib: string; frames: string[]; door: string;
  texture: 'render' | 'stone' | 'brick' | 'adobe'; stones: string[];
}
const MASONRY: Record<MasonryLook, MasonryDef> = {
  render: { face: '#d8cbb0', base: '#b3a587', patch: '#e4dbc6', core: '#9a958a', trim: '#c2bcae', roof: 'flat', roofTop: '#aaa290', tiles: '#aaa290', rib: '#aaa290', frames: ['#4f7a8c', '#5d7a4c', '#7d4e3a'], door: '#5d4a36', texture: 'render', stones: ['#9a958a', '#a9a497', '#d8cbb0'] },
  fieldstone: { face: '#8e8678', core: '#6c665c', trim: '#6b5238', quoin: '#a49b8a', roof: 'pitched', roofTop: '#8e8678', tiles: '#a5583a', rib: '#8f4a31', frames: ['#5a4a3a', '#4c5a3e'], door: '#4e3c2c', texture: 'stone', stones: ['#9d9486', '#837b6e', '#a39a88', '#7a7266'] },
  brick: { face: '#9c5b41', core: '#7a4834', trim: '#cdc4b2', quoin: '#cdc4b2', roof: 'pitched', roofTop: '#9c5b41', tiles: '#555b64', rib: '#474c54', frames: ['#2f4a3a', '#3a3f55'], door: '#3a3f55', texture: 'brick', stones: ['#9c5b41', '#8a4e37', '#a8644a', '#cdc4b2'] },
  adobe: { face: '#b98f64', base: '#9e774f', patch: '#c69c70', core: '#957049', trim: '#5f4630', roof: 'flat', roofTop: '#a5825b', tiles: '#a5825b', rib: '#a5825b', frames: ['#3f6a8a', '#4f8a8a'], door: '#3f6a8a', texture: 'adobe', stones: ['#b98f64', '#9e774f', '#a8835a'] },
};
/** The kinds a look draws (the rest stay the team kit's). */
export const MASONRY_KINDS: ReadonlySet<BaseModule> = new Set<BaseModule>(['wallPlain', 'wallDoor', 'wallWindow', 'wallSlit', 'post', 'postT', 'roof']);
/** A look's colours as the far tier draws a house (rendering/farStructures.ts): its walls' face, its roof's top. */
export const masonryFarColors = (look: MasonryLook): { wall: string; roof: string } => ({ wall: MASONRY[look].face, roof: MASONRY[look].roof === 'pitched' ? MASONRY[look].tiles : MASONRY[look].roofTop });
/** A look's roof (flat with parapets, or pitched: 325, both by look). */
export const masonryRoofOf = (look: MasonryLook): 'flat' | 'pitched' => MASONRY[look].roof;
/** A flat roof's parapet over its slab (model units: a crouched body behind it on the roof is hidden); a pitched roof's rise a unit across. */
const PARAPET_M = 0.6 * U, PITCH = 0.45;
/** A pier's square (a post's place at a node: the walls' ends inside it). */
const PIER = T + 0.14;
/**
 * The z-fight rule (67.65's, the owner on the masonry sheet: "variation stones and patches" and the plinth band fought the
 * face): every face detail stands a real margin proud of the face (`PROUD`), on the face's plane never; no two details
 * overlap (each keeps clear of those laid before it, the trims and the band); the band and the courses stand at depths of
 * their own.
 */
const PROUD = 0.04, BAND_PROUD = 0.05, COURSE_PROUD = 0.025, CLEAR = 0.03;
type Rect = [number, number, number, number];   // x0, x1, y0, y1 on a wall's face
const meets = (a: Rect, b: Rect, m = CLEAR) => a[0] < b[1] + m && a[1] > b[0] - m && a[2] < b[3] + m && a[3] > b[2] - m;
/** A rect less a hole: up to four rects (either side, under, over). */
function less(r: Rect, o: Rect | null): Rect[] {
  if (!o || !meets(r, o, 0)) return [r];
  const out: Rect[] = [], x0 = Math.max(r[0], o[0]), x1 = Math.min(r[1], o[1]);
  if (o[0] > r[0]) out.push([r[0], o[0], r[2], r[3]]);
  if (o[1] < r[1]) out.push([o[1], r[1], r[2], r[3]]);
  if (o[2] > r[2]) out.push([x0, x1, r[2], o[2]]);
  if (o[3] < r[3]) out.push([x0, x1, o[3], r[3]]);
  return out;
}
/** A block on the wall's plane: `r` on the face, `depth` through it (centred on the wall, or at `z`). */
const block = (g: THREE.Object3D, r: Rect, depth: number, c: string, z = 0) => box(g, r[1] - r[0], r[3] - r[2], depth, c, (r[0] + r[1]) / 2, (r[2] + r[3]) / 2, z);

/** The sim's openings, model units on the wall's face (MODULE_SHAPES: the window's and door's 1.2 people, the slit's 1 × 0.3). */
type MasonryWall = 'plain' | 'window' | 'small' | 'door' | 'arch';
const MASONRY_OPENING: Record<MasonryWall, Rect | null> = {
  plain: null,
  window: [-DOOR_W / 2, DOOR_W / 2, WIN0, WIN1],
  small: [-0.5 * U, 0.5 * U, 1.1 * U, 1.4 * U],
  door: [-DOOR_W / 2, DOOR_W / 2, 0, DOOR_H],
  arch: [-DOOR_W / 2, DOOR_W / 2, 0, DOOR_H],
};
/** A segmental arch over the door's opening: its half chord, rise, radius and centre's height. */
const ARCH = (() => { const c = DOOR_W / 2 + 0.1, h = 0.42, R = (c * c + h * h) / (2 * h); return { c, h, R, cy: DOOR_H + h - R }; })();

/**
 * A masonry wall (along x, its faces ±z, its foot at y 0): its face less the sim's opening; the look's plinth band, render
 * patches, stones or brick courses; the opening's trims (a lintel, a sill, a painted frame, `frame` the look's frame colour);
 * a door's leaf open as the team's; an arch's voussoirs and tympanum over the door's opening; the small window's (the slit's)
 * inner step, the sim's. Every detail by the z-fight rule.
 */
function masonryWall(g: THREE.Group, L: MasonryDef, kind: MasonryWall, frame: string, r: () => number): void {
  const o = MASONRY_OPENING[kind], busy: Rect[][] = [[], []];   // (each face's details so far: −z, +z)
  // the face: one profile, the opening cut through it (stacked blocks left faces inside the wall a detail could meet)
  if (!o) front(g, rect(-C / 2, 0, C / 2, H), T, L.face);
  else if (o[2] > 0) front(g, rect(-C / 2, 0, C / 2, H), T, L.face, 0, 0, [rev(rect(o[0], o[2], o[1], o[3]))]);
  else front(g, [-C / 2, 0, o[0], 0, o[0], o[3], o[1], o[3], o[1], 0, C / 2, 0, C / 2, H, -C / 2, H], T, L.face);
  const both = (q: Rect) => { busy[0].push(q); busy[1].push(q); };
  // the trims (laid first: the details keep clear of them)
  // (a trim stands off the opening's head and foot by a margin: flush, its face and the opening's shared a plane)
  if (o && kind !== 'arch') { const q: Rect = [o[0] - 0.15, o[1] + 0.15, o[3] + CLEAR, o[3] + CLEAR + 0.18]; block(g, q, T + 0.08, L.texture === 'stone' || L.texture === 'adobe' ? L.trim : L.quoin ?? L.trim); both(q); }   // the lintel
  if (kind === 'window' || kind === 'small') { const q: Rect = [o![0] - 0.1, o![1] + 0.1, o![2] - CLEAR - 0.09, o![2] - CLEAR]; block(g, q, T + 0.16, L.quoin ?? L.trim); both(q); }        // the sill
  if (kind === 'window' || kind === 'small') for (const x of [o![0] + 0.03, o![1] - 0.03]) { const q: Rect = [x - 0.03, x + 0.03, o![2], o![3]]; block(g, q, T + 0.06, frame); both(q); }   // the painted frame's sides (in the jambs' reveal)
  if (kind === 'door' || kind === 'arch') {
    const leaf = group(g, o![0], 0, T / 2 + 0.04); leaf.rotation.y = -1.75;   // open, swung out (the sim's open leaf)
    box(leaf, DOOR_W - 0.04, DOOR_H - 0.04, 0.07, L.door, DOOR_W / 2, (DOOR_H - 0.04) / 2, 0);
    for (const y of [0.6, 2.2]) box(leaf, DOOR_W - 0.2, 0.08, 0.1, L.trim, DOOR_W / 2, y, 0);
    block(g, [o![0] - 0.15, o![1] + 0.15, 0, 0.12], 0.5, L.quoin ?? L.stones[0], T / 2 + 0.25);   // the step (outside)
    busy[1].push([o![0] - 0.15, o![1] + 0.15, 0, 0.12]);
  }
  if (kind === 'arch') {   // the voussoirs on the segment over the opening, the tympanum (the door's colour) under them
    const { c, R, cy } = ARCH, half = Math.asin(c / R), n = 7;
    for (let k = 0; k < n; k++) {
      const a = -half + (k + 0.5) * (2 * half / n), v = box(g, (2 * half / n) * (R + 0.15) * 0.92, 0.3, T + 0.12, L.quoin ?? L.trim, Math.sin(a) * (R + 0.15), cy + Math.cos(a) * (R + 0.15), 0);
      v.rotation.z = -a;
    }
    for (let k = 0; k < 6; k++) {
      const x0 = -c + k * (2 * c / 6), x1 = x0 + 2 * c / 6, xm = Math.max(Math.abs(x0), Math.abs(x1)), y1 = cy + Math.sqrt(Math.max(0, R * R - xm * xm)) - 0.02;
      if (y1 - DOOR_H - CLEAR > 0.02) block(g, [x0, x1, DOOR_H + CLEAR, y1], T + 0.05, L.door);   // (its face between the wall's and the voussoirs')
    }
    both([-c - 0.35, c + 0.35, DOOR_H, DOOR_H + ARCH.h + 0.35]);
  }
  if (kind === 'small') { box(g, C - PIER, 1 * U, 0.33 * U, L.face, 0, 0.5 * U, -0.285 * U); busy[0].push([-C / 2, C / 2, 0, U]); }   // the inner step (the sim's: a person tall, under the opening)
  // the plinth band, the texture
  const inner = -C / 2 + PIER / 2 + 0.04, outer = C / 2 - PIER / 2 - 0.04;   // (clear of the piers at the nodes)
  const bandH = L.texture === 'adobe' ? 0.35 : 0.5, end = C / 2 - 0.1;   // (a band's or a course's ends inside the piers, and clear of an opening's jambs)
  const jambs: Rect | null = o ? [o[0] - CLEAR, o[1] + CLEAR, o[2] - CLEAR, o[3] + CLEAR] : null;
  if (L.base) { for (const q of less([-end, end, 0, bandH], jambs)) block(g, q, T + 2 * BAND_PROUD, L.base); both([-C / 2, C / 2, 0, bandH]); }
  const lay = (side: number, w: number, h: number, c: string, tries = 4) => {   // a detail on a face, clear of the opening and of every detail laid before it
    for (let t = 0; t < tries; t++) {
      const x = inner + r() * (outer - inner - w), y = 0.1 + r() * (H - 0.3 - h), q: Rect = [x, x + w, y, y + h], b = busy[side > 0 ? 1 : 0];
      if ((o && meets(q, o)) || b.some(p => meets(q, p))) continue;
      b.push(q); box(g, w, h, PROUD + 0.02, c, x + w / 2, y + h / 2, side * (T / 2 + PROUD / 2 - 0.01));
      return;
    }
  };
  if (L.texture === 'render' || L.texture === 'adobe') {
    for (const side of [-1, 1]) for (let k = 0; k < 3; k++) lay(side, 0.4 + r() * 0.7, 0.3 + r() * 0.5, k % 2 ? L.base! : L.patch!);
    if (L.texture === 'adobe') { const cap = box(g, C - 0.2, 0.22, 0.22, L.face, 0, H - 0.04, 0); cap.rotation.x = Math.PI / 4; }   // the rounded top (its ends inside the piers)
  } else if (L.texture === 'stone') {
    for (const side of [-1, 1]) for (let k = 0; k < 12; k++) lay(side, 0.3 + r() * 0.35, 0.18 + r() * 0.16, L.stones[k % L.stones.length]);
  } else {
    for (let y = 0.4; y < H - 0.2; y += 0.55) {   // the courses, cut about the opening and the trims
      const row: Rect = [-end, end, y, y + 0.05];
      let segs = less(row, jambs);
      for (const p of busy[0]) segs = segs.flatMap(q => less(q, [p[0] - CLEAR, p[1] + CLEAR, p[2] - CLEAR, p[3] + CLEAR]));
      for (const q of segs) block(g, q, T + 2 * COURSE_PROUD, L.stones[1]);
      both(row);
    }
  }
}
/** A wall at 0: a stub of its lowest courses and its stones heaped about its footprint, all under the sim's heap. */
function masonryRubble(g: THREE.Group, L: MasonryDef, r: () => number, stub: boolean): void {
  if (stub) for (let x = -C / 2; x < C / 2 - 0.01; x += 0.45) { const h = heapTop() * (0.35 + r() * 0.55); box(g, 0.43, h, T - 0.02, r() < 0.5 ? L.core : L.face, x + 0.225, h / 2, 0); }
  for (let i = 0; i < 20; i++) {
    const s = 0.15 + r() * 0.3, b = box(g, s * (1 + r()), s * 0.6, s, i % 3 === 0 ? L.core : L.stones[i % L.stones.length], (r() - 0.5) * C * 0.9, Math.min(heapTop() - s * 0.3, 0.06 + r() * heapTop() * 0.5), (r() < 0.5 ? -1 : 1) * (T / 2 + 0.1 + r() * 0.8));
    b.rotation.set((r() - 0.5) * 0.5, r() * 3, (r() - 0.5) * 0.5);
  }
}
/** A pier at a grid node (the post's place): a storey tall, quoined (fieldstone, brick: clear of the band), its foot banded (battered for adobe). */
function masonryPier(g: THREE.Group, L: MasonryDef): void {
  box(g, PIER, H, PIER, L.face, 0, H / 2, 0);
  const foot = L.texture === 'adobe' ? 0.9 : 0.5;
  if (L.base) box(g, PIER + 2 * BAND_PROUD, foot, PIER + 2 * BAND_PROUD, L.base, 0, foot / 2, 0);
  if (L.quoin) for (let k = 0; k < 7; k++) { const y = 0.45 + k * 0.45, long = 0.62; box(g, k % 2 ? long : PIER + 0.06, 0.36, k % 2 ? PIER + 0.06 : long, L.quoin, 0, y, 0); }
}

/**
 * A flat roof's cell (`f{mask}`, its open edges: bit 0 −z, 1 +z, 2 −x, 3 +x — no roof and no storey beside it): the slab at the
 * storey's top, a cap course on each open edge (the wall's outer half over the slab's edge) and a parapet on it, the ±z ones
 * over the corners, the ±x ones short of them (two parapets never share a corner's volume); the render's spout, adobe's vigas.
 * Pitched (`p{n}.{i}.{mask}`, the ridge along x: the cell `i` of `n` across it): the slab, the cap courses, two tiled halves at
 * the span's rise (eaves out past an open ±z edge, verges past an open ±x one), the ridge cap on the middle cell, a gable on
 * each open ±x edge. A cell a module: one that falls leaves a hole a cell wide.
 */
function masonryRoof(g: THREE.Group, L: MasonryDef, variant: string): void {
  const pitched = variant[0] === 'p', parts = variant.slice(1).split('.').map(Number), mask = pitched ? parts[2] : parts[0];
  const open = (bit: number) => (mask & (1 << bit)) !== 0;
  box(g, C, SL, C, pitched ? L.core : L.roofTop, 0, H + SL / 2, 0);
  for (let e = 0; e < 4; e++) {   // the cap course on each open edge: the wall's outer half over the slab's edge
    if (!open(e)) continue;
    const alongX = e < 2, s = e % 2 ? 1 : -1, ext = (b: number) => (alongX && open(b) ? T / 2 : 0);   // (the ±z ones over the corners)
    const x0 = -C / 2 - ext(2), x1 = C / 2 + ext(3);
    if (alongX) box(g, x1 - x0, SL, T / 2, L.face, (x0 + x1) / 2, H + SL / 2, s * (C / 2 + T / 4));
    else box(g, T / 2, SL, C, L.face, s * (C / 2 + T / 4), H + SL / 2, 0);
  }
  if (!pitched) {
    for (let e = 0; e < 4; e++) {
      if (!open(e)) continue;
      const alongX = e < 2, s = e % 2 ? 1 : -1;
      const a0 = alongX ? -C / 2 - (open(2) ? T / 2 : 0) : -C / 2 + (open(0) ? T / 2 : 0), a1 = alongX ? C / 2 + (open(3) ? T / 2 : 0) : C / 2 - (open(1) ? T / 2 : 0);
      const p = group(g, alongX ? 0 : s * C / 2, H + SL, alongX ? s * C / 2 : 0); if (!alongX) p.rotation.y = Math.PI / 2;
      const len = a1 - a0, mid = (a0 + a1) / 2 * (alongX ? 1 : -1);   // (turned: the local x is the world's −z)
      box(p, len, PARAPET_M, T, L.face, mid, PARAPET_M / 2, 0);
      if (L.texture === 'adobe') { const c = box(p, len - 2 * CLEAR, 0.22, 0.22, L.face, mid, PARAPET_M - 0.04, 0); c.rotation.x = Math.PI / 4;   /* (its ends short of the parapet's) */ for (const x of [-1.1, 0, 1.1]) box(p, 0.2, 0.2, 0.5, L.trim, x, -SL - 0.25, s * (T / 2 + 0.15)); }   // (the turned group's z is the world's x: out either way)
      else {   // the coping, over the ±z parapets' ends; the ±x ones' short of their overhang (two copings never share a corner)
        const cut = (b: number) => (!alongX && open(b) ? 0.07 : 0), c0 = a0 + cut(0), c1 = a1 - cut(1), cm = (c0 + c1) / 2 * (alongX ? 1 : -1);
        box(p, c1 - c0 + (alongX ? 0.02 : 0), 0.1, T + 0.12, L.quoin ?? L.trim, cm, PARAPET_M + 0.05, 0);
      }
      if (L.texture === 'render' && e === 1) box(p, 0.12, 0.1, 0.6, L.trim, 0.9, 0.05, T / 2 + 0.3);   // a spout (off the parapet's face)
    }
    return;
  }
  const n = parts[0], i = parts[1], half = n * C / 2, EAVE = 0.4, VERGE = 0.3, TH = 0.1, base = H + SL;
  const rise = (b: number) => PITCH * (half - Math.abs((i - (n - 1) / 2) * C + b));   // (b across the cell, from its middle)
  const v0 = open(2) ? VERGE : 0, v1 = open(3) ? VERGE : 0, len = C + v0 + v1, ac = (v1 - v0) / 2;
  for (const [lo0, hi0] of [[-C / 2, 0], [0, C / 2]]) {
    const lo = lo0 === -C / 2 && open(0) ? lo0 - EAVE : lo0, hi = hi0 === C / 2 && open(1) ? hi0 + EAVE : hi0;
    const y0 = base + rise(lo), y1 = base + rise(hi), run = hi - lo, slope = Math.hypot(run, y1 - y0);
    const s = box(g, len, TH, slope, L.tiles, ac, (y0 + y1) / 2 + TH / 2, (lo + hi) / 2); s.rotation.x = -Math.atan2(y1 - y0, run);
    for (const f of [0.33, 0.66]) { const t = box(g, len, 0.05, 0.05, L.rib, ac, y0 + (y1 - y0) * f + TH + 0.03, lo + run * f); t.rotation.x = s.rotation.x; }
  }
  const mid = -(i - (n - 1) / 2) * C;   // (the ridge's b in the cell)
  if ((mid > -C / 2 + 1e-3 && mid < C / 2 - 1e-3) || Math.abs(mid - C / 2) < 1e-3) box(g, len, 0.16, 0.34, L.rib, ac, base + rise(mid) + TH + 0.04, mid);   // the ridge cap
  for (const [bit, x] of [[2, -C / 2], [3, C / 2]] as const) {   // the gables, on the open ends
    if (!open(bit)) continue;
    for (const [lo, hi] of [[-C / 2, 0], [0, C / 2]]) slab(g, [lo, base, hi, base, hi, base + rise(hi), lo, base + rise(lo)], T, L.face, x);
  }
}

/**
 * A masonry module in a look and form (the kit's frame: model units, at the origin): `variant` a wall's `arch` (a door), a
 * window's frame colour (`c{k}`), a roof cell's (`masonryRoof`). Damaged: the kit's damaged pass (scorches, small parts
 * knocked off — stones among them) and chips where the core shows; destroyed: a wall's stub and stones, a pier's, a roof's
 * stones fallen into the room, all under the sim's heap.
 */
export function buildMasonry(kind: BaseModule, look: MasonryLook, variant: string, form: ModuleForm = 'whole', seed = 1): THREE.Group {
  const L = MASONRY[look], g = new THREE.Group(), r = mulberry32(seed * 7919 + MASONRY_LOOKS.indexOf(look) * 131 + BASE_MODULES.indexOf(kind) * 17 + MODULE_FORMS.indexOf(form));
  const frame = L.frames[Number(variant.match(/c(\d+)/)?.[1] ?? 0) % L.frames.length];
  const wall: MasonryWall | null = kind === 'wallPlain' ? 'plain' : kind === 'wallWindow' ? 'window' : kind === 'wallSlit' ? 'small' : kind === 'wallDoor' ? (variant.includes('arch') ? 'arch' : 'door') : null;
  if (form === 'destroyed') {
    if (wall) masonryRubble(g, L, r, true);
    else if (kind === 'roof') masonryRubble(g, { ...L, stones: [L.roofTop, L.tiles, L.core] }, r, false);
    else { box(g, PIER, heapTop() * 0.7, PIER, L.core, 0, heapTop() * 0.35, 0); masonryRubble(g, L, r, false); }
    downToHeap(g);
    return g;
  }
  if (wall) masonryWall(g, L, wall, frame, r);
  else if (kind === 'post' || kind === 'postT') masonryPier(g, L);
  else if (kind === 'roof') masonryRoof(g, L, variant);
  if (form === 'damaged') {
    damageModel(g, seed + 3, 0.6);
    if (wall) for (const side of [-1, 1]) for (let k = 0; k < 5; k++) {   // chips, the core showing (a margin proud of the stones, clear of the opening)
      const w = 0.12 + r() * 0.25, h = 0.1 + r() * 0.2, x = -C / 2 + PIER / 2 + r() * (C - PIER - w), y = 0.6 + r() * (H - 1 - h), o = MASONRY_OPENING[wall];
      if (o && meets([x, x + w, y, y + h], o, 0.05)) continue;
      box(g, w, h, PROUD + 0.05, L.core, x + w / 2, y + h / 2, side * (T / 2 + (PROUD + 0.03) / 2 - 0.01));   // (its front a margin past a stone's)
    }
  }
  return g;
}

const _at = new THREE.Vector3(), _qn = new THREE.Quaternion(), _s3 = new THREE.Vector3(), _up = new THREE.Vector3(0, 1, 0);
/** The kit's shared material: one, white, flat-shaded, its colours per vertex (each module's parts merged). */
const kitMat = markShared(new THREE.MeshLambertMaterial({ color: '#ffffff', flatShading: true, vertexColors: true }));
/** A module's parts merged into one geometry coloured per vertex (each part's material colour), for instancing. */
export function moduleGeometry(kind: BaseModule, p: UnitPalette, form: ModuleForm = 'whole', seed = 1): THREE.BufferGeometry {
  return mergedGeometry(buildModule(kind, p, form, seed));
}
/** A group's meshes merged into one geometry coloured per vertex (each mesh's material colour). */
function mergedGeometry(g: THREE.Group): THREE.BufferGeometry {
  g.updateMatrixWorld(true);
  const geos: THREE.BufferGeometry[] = [];
  g.traverse(o => {
    const me = o as THREE.Mesh;
    if (!me.isMesh || !me.visible) return;
    let geo = me.geometry.clone();
    if (geo.index) geo = geo.toNonIndexed();
    geo.applyMatrix4(me.matrixWorld);
    for (const k of Object.keys(geo.attributes)) if (k !== 'position' && k !== 'normal') geo.deleteAttribute(k);
    const c = ((Array.isArray(me.material) ? me.material[0] : me.material) as THREE.MeshLambertMaterial).color ?? new THREE.Color('#ffffff');
    const n = geo.attributes.position.count, col = new Float32Array(n * 3);
    for (let i = 0; i < n; i++) { col[i * 3] = c.r; col[i * 3 + 1] = c.g; col[i * 3 + 2] = c.b; }
    geo.setAttribute('color', new THREE.BufferAttribute(col, 3));
    geos.push(geo);
  });
  const merged = mergeGeometries(geos, false)!;
  merged.computeBoundingSphere(); merged.computeBoundingBox();
  return merged;
}
/** A module's triangles (its geometry merged). */
export function moduleTriangles(kind: BaseModule, p: UnitPalette, form: ModuleForm = 'whole'): number { return moduleGeometry(kind, p, form).attributes.position.count / 3; }

// ---- 72.3 step 2: a panel wall in four pieces (ARCHITECTURE §5.35; DECISIONS 323, the owner's "Walls in four pieces") -----------

/** The kinds that break in four pieces: the plywood panels (the rest break whole). */
export const PIECE_KINDS: ReadonlySet<BaseModule> = new Set<BaseModule>(['wallPlain', 'wallDoor', 'wallWindow', 'wallSlit']);
/** A panel's pieces, a 2 × 2 — left and right of its middle, under and over `PIECE_SPLIT` — a bit each in a `pieces` mask (set: standing). */
export const WALL_PIECES = { lowerLeft: 1, lowerRight: 2, upperLeft: 4, upperRight: 8 } as const;
export const ALL_PIECES = 15;
/**
 * Where a panel's lower pieces end and its upper ones begin (model units over the module's foot): the window's sill (WIN0,
 * 0.85 people since 326, the slab's 0.15 under it), not the panel's half. At half (1 person) an upper piece lies wholly over a standing eye (0.91 people over the
 * floor) and a lower one is a body's whole height, so a hole would change nothing and a lower piece gone would be a door; at
 * the sill an upper piece gone is a port a standing body sees and fires through with a crouched one hidden under it, as a
 * window is.
 */
export const PIECE_SPLIT = WIN0;
/**
 * §5.35's stages by their pieces: holed (an upper piece gone: a port at eye height, cover under it for a crouched body), a low
 * hole (a lower piece gone: a prone body's), breached (a column gone, its upper and lower piece: a body-wide way through, floor
 * to ceiling), destroyed (none: the heap).
 */
export const WALL_STAGES = { whole: 15, holedLeft: 11, holedRight: 7, lowHole: 14, breached: 10, destroyed: 0 } as const;
const masonryOf = (q: ModulePlacement): boolean => !!q.look && MASONRY_KINDS.has(q.kind);
const piecesOf = (q: ModulePlacement): number => (q.pieces === undefined || !PIECE_KINDS.has(q.kind) ? ALL_PIECES : q.pieces & ALL_PIECES);
/** A placement's prototype: its kind and form, and its pieces when some are gone. */
function placementKey(q: ModulePlacement): string {
  const n = piecesOf(q);
  if (masonryOf(q)) return `${q.kind}@${q.look}${q.variant ? `:${q.variant}` : ''}:${n === 0 ? 'destroyed' : q.form ?? 'whole'}`;
  return n === ALL_PIECES ? `${q.kind}:${q.form ?? 'whole'}` : n === 0 ? `${q.kind}:destroyed` : `${q.kind}:pieces${n}`;
}

/**
 * A non-indexed geometry (position, normal, colour) clipped to one side of the plane `axis` = `at` (0 x, 1 y): each triangle
 * cut by Sutherland–Hodgman, its attributes interpolated along the cut, the polygon left fanned back into triangles.
 */
function clipGeometry(geo: THREE.BufferGeometry, axis: 0 | 1, at: number, keepAbove: boolean): THREE.BufferGeometry {
  const names = ['position', 'normal', 'color'] as const;
  const src = names.map(n => geo.attributes[n].array as Float32Array);
  const out: number[][] = names.map(() => []);
  const side = (v: number) => (keepAbove ? v - at : at - v);
  for (let t = 0; t < src[0].length / 9; t++) {
    const poly: number[][] = [];   // each vertex: position, normal, colour (9 numbers)
    const vert = (i: number) => { const k = (t * 3 + i) * 3; return [...src[0].subarray(k, k + 3), ...src[1].subarray(k, k + 3), ...src[2].subarray(k, k + 3)]; };
    const tri = [vert(0), vert(1), vert(2)];
    for (let i = 0; i < 3; i++) {
      const a = tri[i], b = tri[(i + 1) % 3], da = side(a[axis]), db = side(b[axis]);
      if (da >= 0) poly.push(a);
      if ((da >= 0) !== (db >= 0)) { const f = da / (da - db); poly.push(a.map((x, j) => x + (b[j] - x) * f)); }
    }
    for (let i = 1; i + 1 < poly.length; i++) for (const v of [poly[0], poly[i], poly[i + 1]]) names.forEach((_n, a) => out[a].push(v[a * 3], v[a * 3 + 1], v[a * 3 + 2]));
  }
  const g = new THREE.BufferGeometry();
  names.forEach((n, a) => g.setAttribute(n, new THREE.BufferAttribute(new Float32Array(out[a]), 3)));
  return g;
}

/**
 * A panel wall with some of its pieces gone (`pieces`: the standing ones; 0 the destroyed form, all four the whole one): its
 * damaged form cut to each standing quarter (about x 0 and PIECE_SPLIT), the cut faces capped by jagged splinters where a
 * standing piece meets a gone one, and a low spill of boards under each gone lower piece (under the sim's heap). One
 * prototype a mask in use (at most 14 a kind), built when a field first needs it.
 */
export function pieceGeometry(kind: BaseModule, p: UnitPalette, pieces: number, seed = 1): THREE.BufferGeometry {
  if (pieces === ALL_PIECES || !PIECE_KINDS.has(kind)) return moduleGeometry(kind, p, 'whole', seed);
  if (pieces === 0) return moduleGeometry(kind, p, 'destroyed', seed);
  const whole = moduleGeometry(kind, p, 'damaged', seed), split = PIECE_SPLIT;
  const parts: THREE.BufferGeometry[] = [];
  for (let q = 0; q < 4; q++) {
    if (!(pieces & (1 << q))) continue;
    parts.push(clipGeometry(clipGeometry(whole, 0, 0, (q & 1) === 1), 1, split, (q & 2) === 2));
  }
  whole.dispose();
  const r = mulberry32(seed * 977 + BASE_MODULES.indexOf(kind) * 131 + pieces * 17);
  const g = new THREE.Group();
  const splinters = (x0: number, y0: number, along: 'x' | 'y', length: number, into: number) => {   // a jagged edge: boards and studs broken off into the gone piece
    const n = 5, w = length / n;
    for (let i = 0; i < n; i++) {
      const jag = 0.06 + r() * 0.3, c = i % 2 ? PLY : FRAME;
      const b = along === 'x' ? box(g, w * (0.8 + r() * 0.2), jag, T + 0.02, c, x0 + (i + 0.5) * w, y0 + into * jag / 2, 0) : box(g, jag, w * (0.8 + r() * 0.2), T + 0.02, c, x0 + into * jag / 2, y0 + (i + 0.5) * w, 0);
      b.rotation.z = (r() - 0.5) * 0.25;
    }
  };
  for (let q = 0; q < 4; q++) {
    if (pieces & (1 << q)) continue;
    const left = (q & 1) === 0, upper = (q & 2) === 2, x0 = left ? -C / 2 : 0, y0 = upper ? split : 0;
    if (pieces & (1 << (q ^ 1))) splinters(0, y0, 'y', upper ? H - split : split, left ? -1 : 1);   // the standing piece beside it, across x = 0
    if (pieces & (1 << (q ^ 2))) splinters(x0, split, 'x', C / 2, upper ? 1 : -1);                 // the standing piece over or under it, across the split
    if (!upper) for (let i = 0; i < 4; i++) {                                                // its spill on the floor (under the sim's heap)
      const b = box(g, 0.3 + r() * 0.5, 0.05 + r() * 0.1, 0.15 + r() * 0.3, i % 2 ? PLY_DARK : FRAME, x0 + 0.2 + r() * (C / 2 - 0.4), 0.05, (r() - 0.5) * 1.4);
      b.rotation.set((r() - 0.5) * 0.3, r() * 3, (r() - 0.5) * 0.3);
    }
  }
  if (g.children.length) parts.push(mergedGeometry(g));
  const merged = mergeGeometries(parts, false)!;
  for (const x of parts) x.dispose();
  merged.computeBoundingSphere(); merged.computeBoundingBox();
  return merged;
}

/**
 * One placed module: its kind and form, where (model units: x, y, z) and its turn about y. `stretch` scales its height (a
 * plinth's depth to the terrain: 1 is a person); `open` a gate's leaves, 0 shut to 1 open (default open); `pieces` a panel
 * wall's standing pieces (72.3 step 2).
 */
export interface ModulePlacement { kind: BaseModule; form?: ModuleForm; x: number; y?: number; z: number; yaw?: number; stretch?: number; open?: number;
  /** 72.3 step 2: a panel wall's standing pieces (`PIECE_KINDS`; a bit each, `WALL_PIECES`), absent or all four for the module whole in its form. */
  pieces?: number;
  /** 72.5: drawn in a masonry look (`MASONRY_KINDS`; the rest the team kit's), its `variant` (buildMasonry). Its pieces: none gone or all (72.3 step 5's). */
  look?: MasonryLook; variant?: string }

/** The gates whose leaves turn. */
export type GateKind = 'gate' | 'gateWide';
/**
 * The gates' leaves, in people in the gate's frame (the wall along x, the yard at +z): each leaf a module of its own built on
 * its pivot (`leaf`), hinged at (x, 0) on a jamb and `length` long towards the opening's middle, `height` tall; shut it
 * spans its half of the opening (the hinge at +x turned 0, at −x half round); open it has swung `openAngle` into the yard
 * (+z). For the sim's leaf shape (§5.22: a second shape, there when shut): a box from the hinge, `length` × `height`, turned
 * as `gateLeaves` turns it.
 */
export const GATE_LEAVES: Record<GateKind, { leaf: BaseModule; hinges: [number, number]; length: number; height: number; openAngle: number }> = {
  gate: { leaf: 'gateLeaf', hinges: [-M.door.width / 2, M.door.width / 2], length: M.door.width / 2, height: M.door.height, openAngle: Math.PI / 2 },
  gateWide: { leaf: 'gateWideLeaf', hinges: [-M.vehicleGate.width / 2, M.vehicleGate.width / 2], length: M.vehicleGate.width / 2, height: M.door.height, openAngle: Math.PI / 2 },
};
/** A gate placement's leaves (none for any other module), placed on their hinges and turned by its `open`; a destroyed gate's lie torn off. */
export function gateLeaves(q: ModulePlacement): ModulePlacement[] {
  const g = GATE_LEAVES[q.kind as GateKind];
  if (!g) return [];
  const yaw = q.yaw ?? 0, c = Math.cos(yaw), sn = Math.sin(yaw), open = q.form === 'destroyed' ? 0.3 : q.open ?? 1;
  return g.hinges.map(h => ({ kind: g.leaf, form: q.form, x: q.x + h * U * c, y: q.y, z: q.z - h * U * sn, yaw: yaw + (h > 0 ? 0 : Math.PI) + Math.sign(h) * g.openAngle * open }));
}

/**
 * The field: every placed module drawn instanced — one `InstancedMesh` per kind and form in use (its draw), each geometry
 * built once; a gate brings its leaves (`gateLeaves`), turned by `setOpen`. `scale` takes model units to the world's.
 */
export class BaseKitField {
  readonly group = new THREE.Group();
  readonly meshes = new Map<string, THREE.InstancedMesh>();
  private readonly placements: ModulePlacement[];
  /** Each gate placement's leaves: (their mesh's key, their instance). */
  private readonly leafSlots = new Map<number, Array<[string, number]>>();
  private readonly m4 = new THREE.Matrix4();
  /** 72.5: the masonry prototypes the field drew (merged into its one `masonry` mesh), by key. */
  readonly masonryKeys: string[] = [];
  /** 72.3 step 3: each placement's (mesh key, instance) — `setMatrix` moves it (a field made with `merge` off). */
  private readonly slots: Array<[string, number]> = [];
  /** `merge` off (72.3 step 3: modules that move, a collapse's falls): masonry instanced by its prototypes too, not merged into one draw. */
  constructor(p: UnitPalette, placements: ModulePlacement[], private readonly scale = 1, merge = true) {
    this.placements = [...placements];
    const byKey = new Map<string, ModulePlacement[]>(), masonry = new Map<string, ModulePlacement[]>();
    const put = (q: ModulePlacement): [string, number] => { const k = placementKey(q), into = merge && masonryOf(q) ? masonry : byKey; if (!into.has(k)) into.set(k, []); into.get(k)!.push(q); return [k, into.get(k)!.length - 1]; };
    placements.forEach((q, i) => { this.slots[i] = put(q); const leaves = gateLeaves(q); if (leaves.length) this.leafSlots.set(i, leaves.map(put)); });
    // 72.5: a house's masonry merged into one mesh, one draw (its prototypes are a house's own — a roof cell's open edges, a
    // pitched cell's place in its span — so instanced they were a draw each, 7 a house became 14–17; merged, about 5)
    if (masonry.size) {
      const parts: THREE.BufferGeometry[] = [], m = new THREE.Matrix4();
      for (const [key, list] of masonry) {
        const q0 = list[0], proto = mergedGeometry(buildMasonry(q0.kind, q0.look!, q0.variant ?? '', piecesOf(q0) === 0 ? 'destroyed' : q0.form ?? 'whole'));
        for (const q of list) parts.push(proto.clone().applyMatrix4(m.compose(_at.set(q.x, q.y ?? 0, q.z), _qn.setFromAxisAngle(_up, q.yaw ?? 0), _s3.set(1, q.stretch ?? 1, 1))));
        proto.dispose();
        this.masonryKeys.push(key);
      }
      const geo = mergeGeometries(parts, false)!;
      for (const x of parts) x.dispose();
      geo.computeBoundingSphere(); geo.computeBoundingBox();
      const mesh = new THREE.InstancedMesh(geo, kitMat, 1);
      mesh.name = 'baseKit:masonry';
      mesh.setMatrixAt(0, this.m4.makeScale(scale, scale, scale));
      mesh.instanceMatrix.needsUpdate = true; mesh.computeBoundingSphere();
      this.meshes.set('masonry', mesh); this.group.add(mesh);
    }
    for (const [key, list] of byKey) {
      const q0 = list[0], kind = q0.kind, form = q0.form ?? 'whole';
      const geo = masonryOf(q0) ? mergedGeometry(buildMasonry(kind, q0.look!, q0.variant ?? '', piecesOf(q0) === 0 ? 'destroyed' : form)) : piecesOf(q0) === ALL_PIECES ? moduleGeometry(kind, p, form) : pieceGeometry(kind, p, piecesOf(q0));
      const mesh = new THREE.InstancedMesh(geo, kitMat, list.length);
      mesh.name = `baseKit:${key}`;
      list.forEach((q, i) => mesh.setMatrixAt(i, this.matrixOf(q)));
      mesh.instanceMatrix.needsUpdate = true; mesh.computeBoundingSphere();
      this.meshes.set(key, mesh); this.group.add(mesh);
    }
  }
  private matrixOf(q: ModulePlacement): THREE.Matrix4 {
    const k = this.scale;
    return this.m4.compose(_at.set(q.x * k, (q.y ?? 0) * k, q.z * k), _qn.setFromAxisAngle(_up, q.yaw ?? 0), _s3.set(k, k * (q.stretch ?? 1), k));
  }
  /** Turn placement `i`'s gate leaves to `open` (0 shut … 1 open): its two instances' matrices, nothing rebuilt. */
  setOpen(i: number, open: number): void {
    const slots = this.leafSlots.get(i);
    if (!slots) return;
    this.placements[i] = { ...this.placements[i], open };
    gateLeaves(this.placements[i]).forEach((l, n) => { const mesh = this.meshes.get(slots[n][0])!; mesh.setMatrixAt(slots[n][1], this.matrixOf(l)); mesh.instanceMatrix.needsUpdate = true; mesh.computeBoundingSphere(); });
  }
  /** 72.3 step 3: placement `i` drawn by `m` (model units, the field's scale applied over it) — a falling module's pose this frame; nothing rebuilt. */
  setMatrix(i: number, m: THREE.Matrix4): void {
    const slot = this.slots[i], mesh = slot && this.meshes.get(slot[0]);
    if (!mesh) return;
    mesh.setMatrixAt(slot[1], this.m4.makeScale(this.scale, this.scale, this.scale).multiply(m));
    mesh.instanceMatrix.needsUpdate = true;
  }
  /** The field's cost: its draws (one an instanced mesh), its instances, its triangles (each module's × its count). */
  cost(): { draws: number; triangles: number; instances: number } {
    let triangles = 0, instances = 0;
    for (const m of this.meshes.values()) { triangles += m.geometry.attributes.position.count / 3 * m.count; instances += m.count; }
    return { draws: this.meshes.size, triangles, instances };
  }
  dispose(): void { for (const m of this.meshes.values()) { m.geometry.dispose(); m.dispose(); } this.meshes.clear(); this.group.clear(); }
}

// ---- placing on the grid ------------------------------------------------------------------------------------------------------

/** The cell's side in model units (a grid step). */
export const CELL = C;
/** A wall-like module on the edge from node (i, j) along +x (`'x'`) or +z (`'z'`), its span's cells (one, or a vehicle gate's two). */
export function onEdge(kind: BaseModule, i: number, j: number, along: 'x' | 'z', form?: ModuleForm, y = 0): ModulePlacement {
  const h = spanOf(kind) / 2;
  return along === 'x' ? { kind, form, x: (i + h) * C, y, z: j * C, yaw: 0 } : { kind, form, x: i * C, y, z: (j + h) * C, yaw: Math.PI / 2 };
}
/** A module at node (i, j) (a post). */
export function atNode(kind: BaseModule, i: number, j: number, form?: ModuleForm, y = 0): ModulePlacement { return { kind, form, x: i * C, y, z: j * C }; }
/**
 * A plinth under cell (i, j) (§5.22's slope rule): its top at the column's seat `top` (model units, where the column's floor and
 * walls stand), stretched down to `ground` (the lowest terrain under the cell) and `bury` on into it.
 */
export function plinthUnder(i: number, j: number, top: number, ground: number, form?: ModuleForm, bury = 0.3): ModulePlacement {
  return { kind: 'plinth', form, x: (i + 0.5) * C, y: top, z: (j + 0.5) * C, stretch: Math.max(0.05, (top - ground + bury) / U) };
}
/** A module in cell (i, j) (a tile, stairs, a tower), turned `yaw`; a spanning one (the container room) runs on from it along its turned x. */
export function inCell(kind: BaseModule, i: number, j: number, yaw = 0, form?: ModuleForm, y = 0): ModulePlacement {
  const on = (spanOf(kind) - 1) / 2 * C;
  return { kind, form, x: (i + 0.5) * C + on * Math.cos(yaw), y, z: (j + 0.5) * C - on * Math.sin(yaw), yaw };
}
